import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { useTranslate } from "ra-core";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import type { FinanceKpis } from "../../providers/commons/financeMetrics";
import { useFormatMoney, useFormatPercent } from "../useFormatMoney";

interface KpiProps {
  label: string;
  value: string;
  detail?: string;
  /** Change vs the previous month, shown with an arrow (never color alone) */
  delta?: number | null;
  isAlert?: boolean;
}

const Kpi = ({ label, value, detail, delta, isAlert }: KpiProps) => {
  const formatPercent = useFormatPercent();
  return (
    <Card className="gap-1 px-4 py-3">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p
        className={cn(
          "text-lg font-semibold tabular-nums md:text-xl",
          isAlert && "text-destructive",
        )}
      >
        {value}
      </p>
      <p className="flex items-center gap-1 text-xs text-muted-foreground">
        {delta != null && delta !== 0 && (
          <span className="flex items-center font-medium text-foreground">
            {delta > 0 ? (
              <ArrowUpRight className="size-3" />
            ) : (
              <ArrowDownRight className="size-3" />
            )}
            {formatPercent(Math.abs(delta))}
          </span>
        )}
        {detail}
      </p>
    </Card>
  );
};

/** Headline numbers of the finance overview */
export const KpiCards = ({ kpis }: { kpis: FinanceKpis }) => {
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  const formatPercent = useFormatPercent();
  const mrrDelta =
    kpis.mrrPrevious > 0
      ? (kpis.mrr - kpis.mrrPrevious) / kpis.mrrPrevious
      : null;
  const k = (key: string, options?: Record<string, unknown>) =>
    translate(`crm.finance.overview.kpis.${key}`, options);

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <Kpi
        label={k("mrr")}
        value={formatMoney(kpis.mrr)}
        delta={mrrDelta}
        detail={k("vs_last_month")}
      />
      <Kpi
        label={k("received")}
        value={formatMoney(kpis.receivedThisMonth)}
        detail={k("of_billed", { amount: formatMoney(kpis.billedThisMonth) })}
      />
      <Kpi
        label={k("overdue")}
        value={formatMoney(kpis.overdueAmount)}
        detail={k("delinquency", {
          smart_count: kpis.overdueCount,
          rate: formatPercent(kpis.delinquencyRate),
        })}
        isAlert={kpis.overdueAmount > 0}
      />
      <Kpi
        label={k("net")}
        value={formatMoney(kpis.netThisMonth)}
        detail={k("margin", { margin: formatPercent(kpis.margin) })}
        isAlert={kpis.netThisMonth < 0}
      />
      <Kpi
        label={k("expenses")}
        value={formatMoney(kpis.expensesThisMonth)}
        detail={k("expenses_paid", {
          amount: formatMoney(kpis.expensesPaidThisMonth),
        })}
      />
      <Kpi
        label={k("arpa")}
        value={formatMoney(kpis.arpa)}
        detail={k("active_clients", { smart_count: kpis.activeClients })}
      />
      <Kpi
        label={k("setup_to_receive")}
        value={formatMoney(kpis.setupToReceive)}
        detail={k("active_contracts", { smart_count: kpis.activeContracts })}
      />
      <Kpi
        label={k("concentration")}
        value={formatPercent(kpis.topClientShare)}
        detail={k("concentration_help")}
      />
    </div>
  );
};
